/**
 * @module components/admOrbit
 *
 * The interactive TOGAF ADM navigator.
 *
 * Nine phase nodes sit on a tilted, isometric-style ellipse behind which an SVG
 * scene is drawn. Selecting a node renders the full phase deep-dive — focus,
 * activities, deliverables, quality gate and the AI annotations — into the
 * detail panel beside it.
 *
 * ## Accessibility
 *
 * The orbit is a real WAI-ARIA **tablist**:
 *  - one tab stop (roving `tabindex`), arrow keys / Home / End move between
 *    phases, `aria-selected` tracks the active node, `aria-controls` points at
 *    the panel;
 *  - the panel is `aria-live="polite"` and a separate visually-hidden live
 *    region announces each change;
 *  - nodes are real `<button>` elements, so they are keyboard- and
 *    screen-reader-navigable by default.
 *
 * ## Motion
 *
 * The ambient drift is deliberately bounded: it runs for roughly eleven seconds
 * after load and then settles. It stops immediately on hover, focus or any
 * interaction — a perpetually moving target is hostile to users and to
 * automation. Under `prefers-reduced-motion` there is no drift and no inertia.
 */

import { gsap, EASE, MEDIA, createMotionContext } from "../core/gsap-setup.js";
import { qs, qsa, on, setAttributes } from "../core/dom.js";
import { PHASES, REQUIREMENTS_HUB, getPhaseById } from "../data/phases.js";
import { icon } from "../svg/icons.js";
import { admOrbit as admOrbitScene } from "../svg/scenes.js";

/** How many degrees of orbit rotation a horizontal pixel of drag is worth. */
const DRAG_SENSITIVITY = 0.32;

/**
 * Format a phase for the detail panel.
 *
 * @param {import("../data/phases.js").Phase} phase
 * @returns {string} Markup.
 */
function panelMarkup(phase) {
  const list = (items) => items.map((item) => `<li>${item}</li>`).join("");

  const patterns = phase.ai.patterns
    .map(
      (pattern) => `
      <div class="ai-pattern">
        <p class="ai-pattern__title">${icon("spark", { size: 15 })}${pattern.title}</p>
        <p class="ai-pattern__body">${pattern.body}</p>
        ${pattern.agent ? `<span class="pill pill--accent ai-pattern__agent">${pattern.agent}</span>` : ""}
      </div>`
    )
    .join("");

  return `
    <header class="phase-panel__head">
      <span class="phase-panel__code" aria-hidden="true">${phase.code}</span>
      <h3 class="phase-panel__title" id="phase-panel-title">${phase.name}</h3>
    </header>

    <p class="phase-panel__tagline">${phase.tagline}</p>
    <p class="phase-panel__focus">${phase.focus}</p>
    <p class="phase-panel__narrative">${phase.narrative}</p>

    <div class="phase-panel__grid">
      <div class="phase-list">
        <p class="phase-list__title">Activities</p>
        <ul>${list(phase.activities)}</ul>
      </div>
      <div class="phase-list">
        <p class="phase-list__title">Deliverables</p>
        <ul>${list(phase.deliverables)}</ul>
      </div>
    </div>

    <div class="phase-panel__gate">
      ${icon("check", { size: 18 })}
      <div>
        <strong>Quality gate</strong>
        ${phase.qualityGate}
      </div>
    </div>

    <div class="phase-panel__ai">
      <p class="phase-panel__ai-head">${icon("chip", { size: 15 })} How AI reshapes it</p>
      <p class="phase-panel__ai-headline">${phase.ai.headline}</p>
      <p class="phase-panel__ai-body">${phase.ai.body}</p>
      <div class="ai-patterns">${patterns}</div>
    </div>`;
}

/**
 * @returns {() => void} Teardown.
 */
export function initAdmOrbit() {
  const root = qs("[data-orbit]");
  if (!root) return () => {};

  const ring = qs("[data-orbit-ring]", root);
  const nodeLayer = qs("[data-orbit-nodes]", root);
  const hubArea = qs("[data-orbit-hub]", root);
  const panel = qs("[data-phase-panel]", root);
  const live = qs("[data-phase-live]", root);
  const hint = qs("[data-orbit-hint]", root);

  if (!nodeLayer || !panel) return () => {};

  /* ---------------------------------------------------------------------- */
  /* Static markup                                                           */
  /* ---------------------------------------------------------------------- */

  if (ring) {
    ring.innerHTML = admOrbitScene({ accent: "#5fd4c4", secondary: "#d8a657", width: 980, height: 560 });
  }

  if (hubArea) {
    hubArea.innerHTML = `<strong>RM</strong>${REQUIREMENTS_HUB.name}`;
    hubArea.setAttribute("title", REQUIREMENTS_HUB.tagline);
  }

  nodeLayer.setAttribute("role", "tablist");
  nodeLayer.setAttribute("aria-label", "TOGAF ADM phases");
  nodeLayer.setAttribute("aria-orientation", "horizontal");

  nodeLayer.innerHTML = PHASES.map(
    (phase, index) => `
    <button type="button" class="orbit-node" role="tab" data-phase-node="${phase.id}"
            id="orbit-tab-${phase.id}" aria-controls="phase-panel-region"
            aria-selected="${index === 0}" tabindex="${index === 0 ? "0" : "-1"}"
            style="--phase-accent:${phase.accent}" title="${phase.shortName}">
      <span class="orbit-node__dot" aria-hidden="true">${phase.code}</span>
      <span class="orbit-node__name">${phase.shortName.replace(`${phase.code} · `, "")}</span>
    </button>`
  ).join("");

  const nodes = qsa("[data-phase-node]", nodeLayer);
  const markerNodes = qsa("[data-marker-index]", ring || root);

  /* ---------------------------------------------------------------------- */
  /* Orbit geometry                                                          */
  /* ---------------------------------------------------------------------- */

  // Matches the ellipse drawn in `admOrbit()` (980 × 560 viewBox).
  const RX = 39.4;
  const RY = 26.8;
  const STEP = 360 / PHASES.length;

  /** Current rotation offset of the whole orbit, in degrees. */
  const proxy = { rotation: 0 };

  /**
   * Position every node and marker on the ellipse for the current rotation.
   * Also applies a depth cue: nodes at the back of the ring are smaller and
   * dimmer, which is what makes the flat geometry read as three-dimensional.
   */
  const render = () => {
    nodes.forEach((node, index) => {
      const angle = index * STEP + proxy.rotation;
      const theta = ((angle - 90) * Math.PI) / 180;
      const x = 50 + RX * Math.cos(theta);
      const y = 50 + RY * Math.sin(theta);
      const depth = Math.sin(theta); // 1 = nearest, -1 = furthest
      const closeness = (depth + 1) / 2;
      const isSelected = node.getAttribute("aria-selected") === "true";

      node.style.left = `${x.toFixed(2)}%`;
      node.style.top = `${y.toFixed(2)}%`;
      node.style.zIndex = String(depth > 0 ? 4 : 2);
      // The selected node is never dimmed by the depth cue: the active state has
      // to stay legible wherever it sits on the ring.
      node.style.opacity = isSelected ? "1" : (0.68 + closeness * 0.32).toFixed(3);
      node.style.transform = `translate(-50%, -50%) scale(${(0.87 + closeness * 0.13).toFixed(3)})`;
    });

    markerNodes.forEach((marker, index) => {
      const angle = index * (360 / markerNodes.length) + proxy.rotation;
      const theta = ((angle - 90) * Math.PI) / 180;
      const cx = 50 + RX * 1.045 * Math.cos(theta);
      const cy = 50 + RY * 1.06 * Math.sin(theta);
      // Markers live inside the SVG viewBox, so use viewBox units.
      marker.setAttribute("cx", ((cx / 100) * 980).toFixed(1));
      marker.setAttribute("cy", ((cy / 100) * 560).toFixed(1));
    });
  };

  render();

  /* ---------------------------------------------------------------------- */
  /* Selection                                                               */
  /* ---------------------------------------------------------------------- */

  let currentId = PHASES[0].id;

  /**
   * Select a phase: update the tablist, the panel and every synced view.
   *
   * @param {string} id
   * @param {Object} [options]
   * @param {boolean} [options.focus=false] Move DOM focus to the node.
   * @param {boolean} [options.announce=true] Announce via the live region.
   */
  const select = (id, options = {}) => {
    const phase = getPhaseById(id);
    if (!phase) return;
    const { focus = false, announce = true } = options;

    currentId = id;

    nodes.forEach((node) => {
      const selected = node.getAttribute("data-phase-node") === id;
      setAttributes(node, { "aria-selected": String(selected), tabindex: selected ? "0" : "-1" });
      if (selected && focus) node.focus();
    });

    // Re-tint the panel and its surroundings through one custom property.
    root.style.setProperty("--phase-accent", phase.accent);
    root.style.setProperty("--phase-accent-soft", phase.accentSoft);
    panel.style.setProperty("--phase-accent", phase.accent);

    panel.innerHTML = panelMarkup(phase);

    // Re-apply the depth cue so the newly selected node is undimmed.
    render();

    if (live && announce) {
      live.textContent = `${phase.name}. ${phase.tagline} Focus: ${phase.focus}`;
    }

    // A short, restrained entrance so the change is legible without being noisy.
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      gsap.fromTo(
        panel.children,
        { autoAlpha: 0, y: 14 },
        { autoAlpha: 1, y: 0, duration: 0.5, ease: EASE.out, stagger: 0.045, overwrite: true }
      );
      if (hint) gsap.to(hint, { autoAlpha: 0, duration: 0.3, overwrite: true });
    }
  };

  const selectByIndex = (index) => {
    const phase = PHASES[(index + PHASES.length) % PHASES.length];
    select(phase.id, { focus: true });
  };

  /* ---------------------------------------------------------------------- */
  /* Interaction                                                             */
  /* ---------------------------------------------------------------------- */

  const disposers = [];
  /** @type {gsap.core.Tween|null} */
  let drift = null;

  /**
   * Once the visitor has touched the orbit, the ambient drift never restarts.
   * A diagram that keeps moving under the cursor is hostile to use — and to
   * automation, which cannot click a target that never holds still.
   */
  let userInteracted = false;

  const stopDrift = () => {
    if (!drift) return;
    drift.kill();
    drift = null;
  };

  const markInteraction = () => {
    userInteracted = true;
    stopDrift();
  };

  /**
   * True for the click that follows a drag gesture, so releasing a spin over a
   * node does not also select that node.
   */
  let ignoreNextClick = false;

  /**
   * Select a phase from a pointer gesture.
   *
   * @param {string} id
   */
  const choosePhase = (id) => {
    if (ignoreNextClick) {
      ignoreNextClick = false;
      return;
    }
    markInteraction();
    select(id, {});
  };

  nodes.forEach((node, index) => {
    disposers.push(on(node, "click", () => choosePhase(node.getAttribute("data-phase-node"))));
    disposers.push(on(node, "keydown", (event) => {
      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          event.preventDefault();
          selectByIndex(index + 1);
          break;
        case "ArrowLeft":
        case "ArrowUp":
          event.preventDefault();
          selectByIndex(index - 1);
          break;
        case "Home":
          event.preventDefault();
          selectByIndex(0);
          break;
        case "End":
          event.preventDefault();
          selectByIndex(PHASES.length - 1);
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          choosePhase(node.getAttribute("data-phase-node"));
          break;
        default:
          break;
      }
    }));
    disposers.push(on(node, "focus", markInteraction));
    disposers.push(on(node, "pointerenter", stopDrift));
  });

  /* --- Drag to spin ----------------------------------------------------- */

  /** How far the pointer must travel before a press counts as a drag. */
  const DRAG_THRESHOLD = 6;

  let pointerDown = false;
  let dragActive = false;
  let activePointerId = null;
  let startX = 0;
  let startY = 0;
  let startRotation = 0;
  let lastX = 0;
  let lastTime = 0;
  let velocity = 0;

  const onPointerDown = (event) => {
    if (event.button !== 0) return;
    markInteraction();

    pointerDown = true;
    dragActive = false;
    activePointerId = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
    startRotation = proxy.rotation;
    lastX = event.clientX;
    lastTime = performance.now();
    velocity = 0;
    // A fresh press makes any pending "ignore this click" flag irrelevant.
    ignoreNextClick = false;

    /*
     * Deliberately **no** `setPointerCapture()` here.
     *
     * Capturing on pointerdown retargets the following `click` event to the
     * capturing element — the node layer — so the phase buttons never receive
     * it and a plain click on a phase silently does nothing. Capture is taken
     * lazily below, only once the gesture is unambiguously a drag.
     */
  };

  const onPointerMove = (event) => {
    if (!pointerDown || event.pointerId !== activePointerId) return;

    if (!dragActive) {
      const travelled = Math.hypot(event.clientX - startX, event.clientY - startY);
      if (travelled < DRAG_THRESHOLD) return; // still could be a click

      dragActive = true;
      root.classList.add("is-dragging");

      try {
        nodeLayer.setPointerCapture(event.pointerId);
      } catch {
        // Capture is best-effort: the window listeners below still track the drag.
      }

      /*
       * Reset only the velocity window, so the threshold travel is not mistaken
       * for release speed. The rotation anchor stays at the pointerdown point:
       * re-anchoring here would throw away the distance already travelled, and
       * the very first move of a drag would rotate nothing.
       */
      lastX = event.clientX;
      lastTime = performance.now();
    }

    const now = performance.now();
    const dt = Math.max(now - lastTime, 1);
    velocity = ((event.clientX - lastX) / dt) * 1000;
    lastX = event.clientX;
    lastTime = now;

    proxy.rotation = startRotation + (event.clientX - startX) * DRAG_SENSITIVITY;
    render();
  };

  const onPointerUp = (event) => {
    if (!pointerDown || event.pointerId !== activePointerId) return;

    const wasDragging = dragActive;
    pointerDown = false;
    dragActive = false;
    activePointerId = null;
    root.classList.remove("is-dragging");

    if (nodeLayer.hasPointerCapture?.(event.pointerId)) {
      nodeLayer.releasePointerCapture(event.pointerId);
    }

    // A press that never moved is a click: let the node's own handler run.
    if (!wasDragging) return;

    // The browser still emits a click for this gesture; it must not select.
    ignoreNextClick = true;
    window.setTimeout(() => {
      ignoreNextClick = false;
    }, 400);

    // Inertia, unless the user asked for reduced motion.
    if (Math.abs(velocity) > 60 && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      drift = gsap.to(proxy, {
        rotation: proxy.rotation + velocity * 0.06,
        duration: 1.1,
        ease: "power3.out",
        onUpdate: render,
        onComplete: () => {
          drift = null;
        },
      });
    }
  };

  /* Only wire the drag affordance where a precise pointer exists. On touch
     devices the orbit stays swipe-friendly for the page instead. */
  if (window.matchMedia("(pointer: fine)").matches) {
    disposers.push(on(nodeLayer, "pointerdown", onPointerDown));
    disposers.push(on(window, "pointermove", onPointerMove));
    disposers.push(on(window, "pointerup", onPointerUp));
    disposers.push(on(window, "pointercancel", onPointerUp));
  }

  /*
   * Any pointer entering or focusing the orbit stops the drift for good. This is
   * broader than the per-node handlers above: a visitor aiming at a node should
   * not have to catch a moving target first.
   */
  disposers.push(on(root, "pointerenter", markInteraction));
  disposers.push(on(root, "focusin", markInteraction));

  /* --- Motion context --------------------------------------------------- */

  const mm = createMotionContext();

  mm.add(MEDIA.reduce, () => {
    // No drift, no inertia: the orbit is a static, keyboard-navigable diagram.
    stopDrift();
    if (hint) gsap.set(hint, { autoAlpha: 1 });
    return () => stopDrift();
  });

  mm.add(MEDIA.wide, () => {
    /*
     * A bounded, gentle drift on load — about twelve seconds, then it settles
     * and stays settled. It never starts at all if the visitor has already
     * interacted with the orbit.
     */
    if (userInteracted) return () => stopDrift();

    drift = gsap.to(proxy, {
      rotation: proxy.rotation + 12,
      duration: 6,
      ease: EASE.float,
      repeat: 1,
      yoyo: true,
      onUpdate: render,
      onComplete: () => {
        drift = null;
      },
    });

    return () => stopDrift();
  });

  /* --- Initial selection ------------------------------------------------- */

  select(currentId, { announce: false });

  return () => {
    stopDrift();
    mm.revert();
    disposers.forEach((dispose) => dispose());
  };
}
